import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import type { SocietySummary } from '../services/api';
import 'leaflet/dist/leaflet.css';

function parseLatLong(latlong: string | null | undefined): [number, number] | null {
  if (!latlong || typeof latlong !== 'string') return null;
  const parts = latlong.split(',').map((p) => parseFloat(p.trim()));
  if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
    return [parts[0], parts[1]];
  }
  return null;
}

function distanceKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const toRad = (n: number) => (n * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const aa =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
  return R * c;
}

function escapeHtml(t: string): string {
  return String(t)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface SocietyStepMapProps {
  societies: SocietySummary[];
  societyPincodeFilter: string;
  societyCenter: { lat: number; lng: number } | null;
  societyRangeKm: number;
  selectedSocietyIds: number[];
}

const SocietyStepMap: React.FC<SocietyStepMapProps> = ({
  societies,
  societyPincodeFilter,
  societyCenter,
  societyRangeKm,
  selectedSocietyIds,
}) => {
  const pinTrim = societyPincodeFilter.trim();
  const pinActive = pinTrim.length > 0;
  const locActive = !!societyCenter;
  const filterActive = pinActive || locActive;

  const withCoords = useMemo(() => {
    return societies
      .map((s) => {
        const pos = parseLatLong(s.latlong ?? null);
        return pos ? { society: s, pos } : null;
      })
      .filter(Boolean) as { society: SocietySummary; pos: [number, number] }[];
  }, [societies]);

  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    if (withCoords.length === 0) {
      setMapReady(false);
      return;
    }
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setMapReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [withCoords.length]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  /** Full snapshot so marker styling + popups stay in sync (also triggers rebuild on filter change). */
  const layersKey = useMemo(
    () =>
      JSON.stringify({
        rows: withCoords.map((w) => ({
          id: w.society.id,
          name: w.society.name,
          location: w.society.location,
          city: w.society.city,
          state: w.society.state,
          pincode: w.society.pincode,
          lat: w.pos[0],
          lng: w.pos[1],
        })),
        center: societyCenter,
        rangeKm: societyRangeKm,
        pinFilter: societyPincodeFilter,
        selected: [...selectedSocietyIds].sort((a, b) => a - b),
      }),
    [withCoords, societyCenter, societyRangeKm, societyPincodeFilter, selectedSocietyIds]
  );

  useLayoutEffect(() => {
    if (!mapReady || withCoords.length === 0) return;
    const el = containerRef.current;
    if (!el) return;

    const tearDown = () => {
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch {
          /* ignore */
        }
        mapRef.current = null;
      }
      el.innerHTML = '';
      delete (el as HTMLElement & { _leaflet_id?: number })._leaflet_id;
    };

    tearDown();

    const map = L.map(el, {
      scrollWheelZoom: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const boundsPoints: L.LatLngExpression[] = [];

    if (societyCenter) {
      L.circle([societyCenter.lat, societyCenter.lng], {
        radius: societyRangeKm * 1000,
        color: '#0d9488',
        fillColor: '#14b8a6',
        fillOpacity: 0.12,
        weight: 2,
      }).addTo(map);
      L.circleMarker([societyCenter.lat, societyCenter.lng], {
        radius: 10,
        color: '#0f766e',
        fillColor: '#14b8a6',
        fillOpacity: 0.95,
        weight: 2,
      })
        .addTo(map)
        .bindTooltip(`Your location (${societyRangeKm} km range)`, {
          direction: 'top',
          offset: [0, -8],
        });
      boundsPoints.push([societyCenter.lat, societyCenter.lng]);
    }

    const isHighlighted = (s: SocietySummary, pos: [number, number]): boolean => {
      if (!filterActive) return false;
      if (pinActive && (s.pincode ?? '').toString().startsWith(pinTrim)) return true;
      if (locActive && societyCenter) {
        if (distanceKm([societyCenter.lat, societyCenter.lng], pos) <= societyRangeKm) return true;
      }
      return false;
    };

    withCoords.forEach(({ society: s, pos }) => {
      const highlighted = isHighlighted(s, pos);
      const dimmed = filterActive && !highlighted;
      const r = highlighted ? 14 : dimmed ? 6 : 9;
      const fill = highlighted ? '#0d9488' : dimmed ? '#94a3b8' : '#64748b';
      const stroke = highlighted ? '#0f766e' : '#475569';
      const addr = [s.location, s.city, s.state].filter(Boolean).join(', ');
      const dist =
        locActive && societyCenter
          ? distanceKm([societyCenter.lat, societyCenter.lng], pos).toFixed(1)
          : null;
      const selected = selectedSocietyIds.includes(s.id) || selectedSocietyIds.includes(-1);

      const pinMatch = pinActive && (s.pincode ?? '').toString().startsWith(pinTrim);
      const inRange =
        locActive &&
        societyCenter &&
        distanceKm([societyCenter.lat, societyCenter.lng], pos) <= societyRangeKm;

      let popup = `<div class="society-map-popup"><strong>${escapeHtml(s.name)}</strong>`;
      if (selected) {
        popup += '<span class="society-map-popup-badge">Selected</span>';
      }
      popup += `<p class="society-map-popup-line">${escapeHtml(addr || '—')}</p>`;
      if (s.pincode != null && String(s.pincode) !== '') {
        popup += `<p class="society-map-popup-line"><strong>Pincode:</strong> ${escapeHtml(String(s.pincode))}</p>`;
      }
      if (dist != null) {
        popup += `<p class="society-map-popup-line"><strong>Distance:</strong> ${escapeHtml(dist)} km from you</p>`;
      }
      if (pinMatch) {
        popup += '<p class="society-map-popup-match">Matches pincode filter</p>';
      }
      if (inRange) {
        popup += '<p class="society-map-popup-match">Within location range</p>';
      }
      popup += '</div>';

      const tip = `${s.name}${highlighted ? ' · match' : ''}`;
      L.circleMarker([pos[0], pos[1]], {
        radius: r,
        color: stroke,
        fillColor: fill,
        fillOpacity: dimmed ? 0.55 : 0.9,
        weight: highlighted ? 3 : 2,
      })
        .addTo(map)
        .bindTooltip(tip, { direction: 'top', offset: [0, -6] })
        .bindPopup(popup);
      boundsPoints.push([pos[0], pos[1]]);
    });

    if (boundsPoints.length === 1) {
      map.setView(boundsPoints[0], 14);
    } else if (boundsPoints.length > 1) {
      map.fitBounds(L.latLngBounds(boundsPoints), { padding: [40, 40], maxZoom: 14 });
    }

    const t = window.setTimeout(() => {
      map.invalidateSize();
    }, 200);

    return () => {
      window.clearTimeout(t);
      tearDown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layersKey snapshots societies, filters, selection
  }, [mapReady, layersKey]);

  if (withCoords.length === 0) {
    return (
      <div className="society-step-map-empty">
        <p>No societies have map coordinates yet. Add lat/long for societies to see them on the map.</p>
      </div>
    );
  }

  return (
    <div className="society-step-map-wrap">
      <p className="society-step-map-legend">
        {filterActive ? (
          <>
            <span className="legend-dot legend-dot--highlight" /> Filter match &nbsp;·&nbsp;
            <span className="legend-dot legend-dot--normal" /> Other societies
          </>
        ) : (
          <>All societies shown. Use pincode or location filter to highlight matches.</>
        )}
      </p>
      {!mapReady ? (
        <div className="society-step-map society-step-map--loading" aria-hidden>
          Loading map…
        </div>
      ) : (
        <div ref={containerRef} className="society-step-map" />
      )}
    </div>
  );
};

export default SocietyStepMap;
